import {
  BodyText,
  HeaderText,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import { Eye } from "lucide-react";
import React, { useMemo, useState } from "react";

import { QUESTIONNAIRE_NAMES } from "@/types/questionnaires";
import { camelCase } from "change-case";
import { OpenChartButton } from "./components/OpenChartButton";
import { TaskListRow } from "./components/TaskListRow";
import { DownloadMultipleReports } from "./components/Unsolicited/DownloadMultipleReports";
import { DownloadReport } from "./components/Unsolicited/DownloadReport";
import { MarkResolved } from "./components/Unsolicited/MarkResolved";
import {
  TASK_TYPE_LABELS,
  type TaskItem,
  TaskListItemType,
  UnsolicitedReason,
  isLabFailed,
  isLabNeedsSubmission,
  isLabResult,
  isQuestionnaireResponseReview,
  isRxError,
  isUnsolicitedLab,
  useTaskList,
} from "./network/useTaskList";
import {
  TaskListAction,
  useTaskListAvailableActions,
} from "./network/useTaskListAvailableActions";
import {
  type TaskListCountResponse,
  useTaskListCount,
} from "./network/useTaskListCount";

const ALL_TASKS_FILTER = "all";

export const TaskList = () => {
  const { data, isFetched, isFetching, isError } = useTaskList();
  const { data: countsData, isLoading: isLoadingCounts } = useTaskListCount();
  const { data: availableActions } = useTaskListAvailableActions();

  const canResolveUnsolicitedLab =
    availableActions?.[TaskListAction.RESOLVE_UNSOLICITED_LAB]?.allowed ??
    false;
  const resolveUnsolicitedLabNotAllowedReason =
    availableActions?.[TaskListAction.RESOLVE_UNSOLICITED_LAB]
      ?.notAllowedReason ??
    "You are not authorized to resolve unsolicited labs";

  const [selectedFilter, setSelectedFilter] =
    useState<string>(ALL_TASKS_FILTER);

  const tasks = data?.taskListItems ?? [];

  const filteredTasks: TaskItem[] = useMemo(() => {
    if (selectedFilter === ALL_TASKS_FILTER) {
      return tasks;
    }
    return tasks.filter((task) => task.itemType === selectedFilter);
  }, [selectedFilter, tasks]);

  const filterOptions = useMemo(() => {
    const options = [
      {
        value: ALL_TASKS_FILTER,
        label: "All tasks",
        count: countsData?.totalTaskCount ?? 0,
      },
    ];
    for (const type of Object.values(TaskListItemType)) {
      options.push({
        value: type,
        label: TASK_TYPE_LABELS[type],
        count:
          countsData?.[
            `${camelCase(type)}TasksCount` as keyof TaskListCountResponse
          ] ?? 0,
      });
    }
    return options;
  }, [countsData]);

  const renderTask = (task: TaskItem | undefined) => {
    if (!task) return undefined;

    if (isRxError(task)) {
      return (
        <TaskListRow
          key={`${task.itemType}-${task.prescription.id}`}
          identifier={task.prescription.id}
          message={`${task.prescription.medicationName} failed to route to the pharmacy.`}
          patientName={`${task.patient.nameFirst} ${task.patient.nameLast}`}
          patientState={task.patient.state}
          taskDueTimestamp={task.taskDueAt}
          type={TaskListItemType.RX_ERRORS}
          actionButtons={[
            <OpenChartButton
              key="open-chart"
              patientId={task.patient.id}
              tabName="medications-allergies"
            />,
          ]}
        />
      );
    }

    if (isLabResult(task)) {
      return (
        <TaskListRow
          key={`${task.itemType}-${task.serviceRequestId}`}
          identifier={task.serviceRequestId}
          message={`Results ready to view for ${task.labTestName}`}
          patientName={`${task.patient.nameFirst} ${task.patient.nameLast}`}
          patientState={task.patient.state}
          taskDueTimestamp={task.taskDueAt}
          type={TaskListItemType.LAB_RESULTS}
          actionButtons={[
            <OpenChartButton
              key="open-chart"
              patientId={task.patient.id}
              tabName="labs"
            />,
          ]}
        />
      );
    }

    if (isUnsolicitedLab(task)) {
      const actionButtons = [];
      if (task.curaiPatientId) {
        actionButtons.push(
          <OpenChartButton
            key="open-chart"
            patientId={task.curaiPatientId as string}
            tabName="labs"
          />,
        );
      }

      const message =
        task.unsolicitedReason === UnsolicitedReason.ORDER_MATCH_FAILED
          ? "A lab report not associated with a Curai order has been received."
          : "A lab report not associated with a Curai patient has been received.";

      if (task.pdfUrls && task.pdfUrls.length === 1) {
        actionButtons.push(<DownloadReport pdfUrl={task.pdfUrls[0]} />);
      } else if (task.pdfUrls && task.pdfUrls.length > 1) {
        actionButtons.push(<DownloadMultipleReports pdfUrls={task.pdfUrls} />);
      }
      actionButtons.push(
        <MarkResolved
          diagnosticReportId={task.diagnosticReportId}
          disabled={!canResolveUnsolicitedLab}
          disabledReason={resolveUnsolicitedLabNotAllowedReason}
          patientId={task.curaiPatientId}
        />,
      );
      return (
        <TaskListRow
          key={`${task.itemType}-${task.diagnosticReportId}`}
          identifier={task.diagnosticReportId}
          message={message}
          patientName={task.patientName}
          patientState={undefined}
          taskDueTimestamp={task.taskDueAt}
          type={TaskListItemType.UNSOLICITED_LABS}
          actionButtons={actionButtons}
        />
      );
    }

    if (isLabFailed(task)) {
      return (
        <TaskListRow
          key={`${task.itemType}-${task.requestGroupId}`}
          identifier={task.requestGroupId}
          message="A lab order failed to route to the lab."
          patientName={`${task.patient.nameFirst} ${task.patient.nameLast}`}
          patientState={task.patient.state}
          taskDueTimestamp={task.taskDueAt}
          type={TaskListItemType.LAB_FAILED}
          actionButtons={[
            <OpenChartButton
              key="open-chart"
              patientId={task.patient.id}
              tabName="labs"
            />,
          ]}
        />
      );
    }

    if (isLabNeedsSubmission(task)) {
      return (
        <TaskListRow
          key={`${task.itemType}-${task.requestGroupId}`}
          identifier={task.requestGroupId}
          message="Patient has paid for an async lab. Please submit order."
          patientName={`${task.patient.nameFirst} ${task.patient.nameLast}`}
          patientState={task.patient.state}
          taskDueTimestamp={task.taskDueAt}
          type={TaskListItemType.LAB_NEEDS_SUBMISSION}
          actionButtons={[
            <OpenChartButton
              key="open-chart"
              patientId={task.patient.id}
              tabName="labs"
            />,
          ]}
        />
      );
    }

    if (isQuestionnaireResponseReview(task)) {
      const questionnaireInfo =
        QUESTIONNAIRE_NAMES[
          task.questionnaireId as keyof typeof QUESTIONNAIRE_NAMES
        ];
      const displayName =
        questionnaireInfo?.displayName || task.questionnaireId;
      const shorthandName =
        questionnaireInfo?.shorthandName || task.questionnaireId;
      const message = `${displayName} (${shorthandName}) screening has been completed: ${task.questionnaireScore}`;

      return (
        <TaskListRow
          key={`${task.itemType}-${task.taskId}`}
          identifier={task.taskId}
          message={message}
          patientName={`${task.patient.nameFirst} ${task.patient.nameLast}`}
          patientState={task.patient.state}
          taskDueTimestamp={task.taskDueAt}
          type={TaskListItemType.QUESTIONNAIRE_RESPONSE_REVIEW}
          actionButtons={[
            <OpenChartButton
              key={`open-questionnaire-${task.taskId}`}
              patientId={task.patient.id}
              tabName="health-maintenance"
              search={
                task.questionnaireResponseId
                  ? {
                      questionnaireResponseId: task.questionnaireResponseId,
                      questionnaireType: task.questionnaireId,
                    }
                  : { openQuestionnaire: task.questionnaireId }
              }
              buttonText="Review"
              icon={Eye}
            />,
          ]}
        />
      );
    }

    return undefined;
  };

  return (
    <div className="p-6 h-full overflow-auto pb-12">
      <div className="flex items-center justify-between mb-4 gap-4">
        <HeaderText size="lg">Task List</HeaderText>
        {tasks.length > 0 && !isLoadingCounts && (
          <Select
            value={selectedFilter}
            onValueChange={(value) => setSelectedFilter(value)}
          >
            <SelectTrigger
              aria-label="filter-task-type"
              className="max-w-60 min-w-[12rem]"
            >
              <SelectValue placeholder="Filter by task type" />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectGroup>
                {filterOptions
                  .filter((option) =>
                    option.value === ALL_TASKS_FILTER
                      ? option.count >= 0
                      : option.count > 0,
                  )
                  .map((option, index) => (
                    <React.Fragment key={option.value}>
                      {index === 1 && <SelectSeparator />}
                      <SelectItem
                        value={option.value}
                        rightSlot={option.count || undefined}
                      >
                        {option.label}
                      </SelectItem>
                    </React.Fragment>
                  ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}
      </div>

      <section className="mt-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <BodyText bold={true} size="sm" textColor="tertiary">
                  Type
                </BodyText>
              </TableHead>
              <TableHead>
                <BodyText bold={true} size="sm" textColor="tertiary">
                  Patient
                </BodyText>
              </TableHead>
              <TableHead>
                <BodyText bold={true} size="sm" textColor="tertiary">
                  State
                </BodyText>
              </TableHead>
              <TableHead>
                <BodyText bold={true} size="sm" textColor="tertiary">
                  Message
                </BodyText>
              </TableHead>
              <TableHead>
                <BodyText bold={true} size="sm" textColor="tertiary">
                  Time
                </BodyText>
              </TableHead>
              <TableHead>
                <BodyText bold={true} size="sm" textColor="tertiary">
                  Actions
                </BodyText>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody
            isError={isError}
            isLoading={!isFetched && isFetching}
            columnCount={6}
          >
            {filteredTasks.map((task) => renderTask(task))}
          </TableBody>
        </Table>
      </section>
    </div>
  );
};
